import { hashStringToColor } from '@go-shared/lib/utils';
import { VenueRequestFile } from '@go-shared/types/groupBooking';
import React from 'react';

import {
  BubbleBadge,
  BubbleButton,
  BubbleFormGroup,
  BubbleInput,
  BubbleTextArea,
  BubbleFileM,
} from '../BubbleUI';
import { FormGenericContainer } from '../FormGenericContainer';
import { confirmFormExit, SubgroupForm } from './Workflows';
import { getCommonTexts } from '@go-shared/lib/brand';
import { UPLOAD_RULES } from '@go-shared/lib/uploads';
import { useServerInfo } from '@go-client/context/ServerInfoContext';

interface EnterSubgroupPageProps {
  subgroupForm: SubgroupForm;
  changeSubgroupForm: <T extends keyof SubgroupForm>(key: T, val: SubgroupForm[T]) => void;
  title?: string;
  pageIndex?: number;
  onClose?: () => void;
  onSubmitSubgroupDetails: () => void;
  onClearSubgroupForm?: () => void;
  onResetSubgroupForm?: () => void;
  onUpdateFile: (evt: VenueRequestFile) => void;
  onUploadFile: (file: File) => void;
  onDeleteFile: (file: VenueRequestFile) => void;
}

export default function EnterSubgroupPage(props: EnterSubgroupPageProps) {
  const { brand } = useServerInfo();
  const { tour_leader, tour_leader_cabin } = getCommonTexts(
    {
      default: brand.commonTexts,
      translations: [],
    },
    { selected: 'en' }
  );

  return (
    <div className="tflf-1">
      <FormGenericContainer
        className="h-screen flex flex-col"
        bodyClassName="flex-grow flex flex-col"
        footer={() => (
          <>
            <div className="text-slate-dark"></div>
            <BubbleButton
              className="ml-auto"
              variant="navy"
              onClick={props.onSubmitSubgroupDetails}
            >
              Finish: Submit Subgroup Details
            </BubbleButton>
          </>
        )}
      >
        <div className="flex items-start mb-5">
          <div className="mr-auto">
            <div className="flex items-center">
              <h1 className="text-lg text-slate-dark font-semibold">
                <span>{props.title || 'Edit Subgroup'}</span>
              </h1>
              <BubbleBadge
                variant="smoke"
                className="ml-2"
                color={hashStringToColor(props.subgroupForm.label)}
              >
                {props.subgroupForm.label}
              </BubbleBadge>
            </div>
            <h2 className="text-2xl text-navy">
              {props.pageIndex !== undefined ? `${props.pageIndex}. ` : ''}Enter Subgroup Details
            </h2>
          </div>
          <BubbleButton
            variant="navy"
            inverse
            onClick={() => confirmFormExit(props.subgroupForm._dirty, props.onClose)}
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <BubbleFormGroup label="Subgroup Name" className="col-span-3">
            <BubbleInput
              type="text"
              block
              value={props.subgroupForm.name}
              onValue={val => props.changeSubgroupForm('name', val)}
            />
          </BubbleFormGroup>
          <BubbleFormGroup label={tour_leader}>
            <BubbleInput
              type="text"
              block
              value={props.subgroupForm.groupGuideName}
              onValue={val => props.changeSubgroupForm('groupGuideName', val)}
            />
          </BubbleFormGroup>
          <BubbleFormGroup label={`${tour_leader_cabin} / Booking No.`}>
            <BubbleInput
              type="text"
              block
              value={props.subgroupForm.groupGuideCabin}
              onValue={val => props.changeSubgroupForm('groupGuideCabin', val)}
            />
          </BubbleFormGroup>
          <BubbleFormGroup label="Attendees in Group (min. 0)">
            <BubbleInput
              type="number"
              min={0}
              block
              value={props.subgroupForm.attendees}
              onValue={val => props.changeSubgroupForm('attendees', val)}
            />
          </BubbleFormGroup>
          <BubbleFormGroup label="Notes" className="col-span-3">
            <BubbleTextArea
              block
              value={props.subgroupForm.notes}
              onValue={val => props.changeSubgroupForm('notes', val)}
            />
          </BubbleFormGroup>
          <div className="col-span-3">
            <BubbleFileM
              label="Subgroup Files"
              files={props.subgroupForm.files}
              onUpdateFile={props.onUpdateFile}
              onUploadFile={props.onUploadFile}
              uploadRule={UPLOAD_RULES.groupFile}
              hintPosition="inside"
              onDeleteFile={props.onDeleteFile}
            />
          </div>
        </div>
      </FormGenericContainer>
    </div>
  );
}
